/******************************************************************
 * ##: LocationStatusIndicator
 * Discrete GPS status chip shown in the header. Mirrors the app style (antd),
 * shows a friendly label per status and, when relevant, the number of positions
 * still pending synchronization. Never surfaces technical errors to the user.
 *
 * TL;DR: Small colored GPS status badge for the header.
 * History:
 * 23-07-2026: Created
 ******************************************************************/

import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "antd";
import { EnvironmentTwoTone, EnvironmentOutlined, LoadingOutlined } from "@ant-design/icons";
import { LOCATION_STATUS } from "constants/location";

const STATUS_META = {
  [LOCATION_STATUS.ACTIVE]: { color: "#52c41a", label: "GPS ativo" },
  [LOCATION_STATUS.WAITING]: { color: "#faad14", label: "A aguardar posição" },
  [LOCATION_STATUS.UNAVAILABLE]: { color: "#bfbfbf", label: "Localização indisponível" },
  [LOCATION_STATUS.DENIED]: { color: "#ff4d4f", label: "Permissão de localização recusada" },
  [LOCATION_STATUS.IDLE]: { color: "#742020", label: "GPS inativo" },
};

const LocationStatusIndicator = ({ status, pendingCount }) => {
  const meta = STATUS_META[status] || STATUS_META[LOCATION_STATUS.IDLE];
  const isWaiting = status === LOCATION_STATUS.WAITING;

  const icon = isWaiting ? (
    <LoadingOutlined style={{ color: meta.color }} />
  ) : status === LOCATION_STATUS.ACTIVE ? (
    <EnvironmentTwoTone twoToneColor={meta.color} />
  ) : (
    <EnvironmentOutlined style={{ color: meta.color }} />
  );

  const tooltip = pendingCount > 0 ? `${meta.label} (${pendingCount} por sincronizar)` : meta.label;

  // Mirror the Sync ("Sincronizado") indicator structure so it lines up with the other
  // header items (sync status, network, avatar). The pending count lives in the tooltip.
  return (
    <div className="mr-3">
      <Tooltip title={tooltip} placement="left">
        {icon}
        <span style={{ marginLeft: 5 }}>{meta.label}</span>
      </Tooltip>
    </div>
  );
};

LocationStatusIndicator.propTypes = {
  status: PropTypes.string,
  pendingCount: PropTypes.number,
};

LocationStatusIndicator.defaultProps = {
  status: LOCATION_STATUS.IDLE,
  pendingCount: 0,
};

export default LocationStatusIndicator;
